import { Ban, CheckCircle2, FolderLock, Gavel, History, Lock, RotateCcw, Settings, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable } from '../../../../components/data-table/useDataTable';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { Menu, type MenuItem } from '../../../../components/Menu';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { CaseAccessLevelModal } from '../components/CaseAccessLevelModal';
import { CaseFilesPanel } from '../components/CaseFilesPanel';
import { CaseMembersModal } from '../components/CaseMembersModal';
import { CaseModal } from '../components/CaseModal';
import { CaseReportsPanel } from '../components/CaseReportsPanel';
import { caseFilesApi, casesApi } from '../casesApi';
import {
  CASE_AUDIT_ENTITY_TYPES,
  CASE_FILE_MAX_BYTES,
  CASE_STATUS_LABEL,
  FLAG,
  FLAG_NAMES,
  hasFlag,
  type CaseListItem,
  type CaseMemberRow,
  type CaseMemberSummary,
} from '../caseTypes';
import { heldEvidenceReason } from '../utils/caseReasons';

type TabKey = 'info' | 'attachments' | 'reports' | 'team';

/**
 * One registered case — its fields, its classification, its files, its Post Operation Reports,
 * and who it is shared with.
 *
 * <b>Every control carries the KEY of what it does and is withheld or disabled by the case's own
 * FLAG.</b> The key says the account may do this kind of thing anywhere; the flag says whether
 * this case lets them. A key alone opens nothing — the server checks both.
 *
 * <b>Opening this page writes no evidence access row</b> — nothing filed under the case is read
 * here. Its evidence is one link away, on the Evidence page, which logs its own opening.
 * A case above the reader's access level is a 403, and the page says so instead of rendering.
 */
export function CaseProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [record, setRecord] = useState<CaseListItem | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [tab, setTab] = useState<TabKey>('info');
  const [modal, setModal] = useState<'edit' | 'level' | 'share' | 'history' | 'delete' | 'restore' | null>(null);
  const [acting, setActing] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [membersKey, setMembersKey] = useState(0);
  const files = useMemo(() => caseFilesApi(id ?? ''), [id]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      casesApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setRecord(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this case.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    // A different case drops what is on screen first.
    setRecord(null);
    setError(null);

    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const breadcrumb: BreadcrumbSegment[] = [
    'Case Management',
    { label: 'Cases', to: '/cases' },
    record?.caseNumber ?? 'Case',
  ];

  if (error && !record) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        {error.isForbidden ? (
          <EmptyState message="This case is above your access level. It is listed for you because you are a member, but it can be opened only by members cleared for its level." />
        ) : (
          <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
        )}
      </div>
    );
  }

  if (!record) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  const flags = record.permissions;

  async function deactivate(reauthentication?: string) {
    if (!record) return;

    setActing(true);
    setActionError(null);

    try {
      await casesApi.deactivate(record.id, reauthentication ?? '');
      toast.success(`${record.caseNumber} was deleted.`);
      setModal(null);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The case could not be deleted.', 0));
    } finally {
      setActing(false);
    }
  }

  async function restore() {
    if (!record) return;

    setActing(true);
    setActionError(null);

    try {
      await casesApi.activate(record.id);
      toast.success(`${record.caseNumber} was restored.`);
      load();
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught : new ApiError('The case could not be restored.', 0));
    } finally {
      setActing(false);
    }
  }

  const actions: MenuItem[] = [
    /*
      THE CASE'S EVIDENCE is another module's surface — linked, not drawn here. Its own key, so a
      registry clerk who cannot read evidence is not offered a page that would refuse them.
    */
    {
      key: 'evidence',
      label: 'Open evidence',
      icon: <FolderLock className="h-4 w-4" aria-hidden="true" />,
      permission: 'evidence-items.read',
      onClick: () => navigate(`/evidence/cases/${record.id}`),
    },
    {
      key: 'history',
      label: 'History Logs',
      icon: <History className="h-4 w-4" aria-hidden="true" />,
      permission: 'audit-logs.read',
      onClick: () => setModal('history'),
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
      permission: 'cases.delete',
      destructive: true,
      disabledReason: !record.isActive
        ? 'This case is already deleted.'
        : !hasFlag(flags, FLAG.delete)
          ? 'You have not been given Delete on this case.'
          : heldEvidenceReason(record.heldEvidenceCount),
      onClick: () => setModal('delete'),
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell<TabKey>
        id="case-profile"
        panel={tab === 'info' ? 'cards' : 'bleed'}
        header={
          <ProfileHeader
            id="case-profile-header"
            icon={<Gavel className="h-6 w-6" aria-hidden="true" />}
            title={record.title}
            meta={[record.caseNumber, CASE_STATUS_LABEL[record.status], record.accessLevelName ?? 'Unclassified']}
            status={
              <StatusText tone={record.isActive ? 'active' : 'inactive'}>{record.isActive ? 'Active' : 'Deleted'}</StatusText>
            }
            createdBy={record.createdByName}
            createdAt={record.createdAt}
            actions={
              <Menu
                id="case-profile-settings"
                items={actions}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    id="case-profile-settings-trigger"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Case settings"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          actionError ? (
            <ProfileBanner error id="case-profile-banner" icon={<Ban className="h-4 w-4" aria-hidden="true" />} message={actionError.message} />
          ) : !record.isActive ? (
            <ProfileBanner
              id="case-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This case is deleted. It no longer appears in the case lists; its members, evidence access log and custody records are kept."
              action={
                hasFlag(flags, FLAG.delete) ? (
                  <PermissionGate permission="cases.delete">
                    <Button
                      id="case-profile-restore"
                      variant="secondary"
                      leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => setModal('restore')}
                      disabled={acting}
                      loading={acting}
                      loadingLabel="Restoring"
                    >
                      Restore
                    </Button>
                  </PermissionGate>
                ) : undefined
              }
            />
          ) : (
            <ProfileBanner
              id="case-profile-banner"
              icon={record.accessLevelId != null ? <Lock className="h-4 w-4" aria-hidden="true" /> : <CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message={
                record.accessLevelId != null
                  ? `This case is classified ${record.accessLevelName}. Members below that level see it listed with its title masked, and cannot open it or anything filed under it.`
                  : 'This case is unclassified. Every member it is shared with can open it and everything filed under it.'
              }
            />
          )
        }
        tabs={[
          { key: 'info', label: 'Info' },
          { key: 'attachments', label: 'Attachments', permission: 'cases.read' },
          // Another module's rows on this case — its own read key, so a registry clerk without it
          // gets no tab rather than a refused fetch.
          { key: 'reports', label: 'Post Operation Reports', permission: 'post-operation-reports.read' },
          // No count: the team arrives when the tab opens, and a badge filled in late states nothing.
          { key: 'team', label: 'Team', permission: 'cases.read' },
        ]}
        activeTab={tab}
        onTabChange={setTab}
      >
        {tab === 'info' ? (
          <>
            <ProfileCard
              id="case-profile-details"
              title="Case"
              menuItems={[
                {
                  key: 'edit',
                  label: 'Edit',
                  permission: 'cases.update',
                  disabledReason: hasFlag(flags, FLAG.edit) ? null : 'You have not been given Edit on this case.',
                  onClick: () => setModal('edit'),
                },
              ]}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Case number', value: record.caseNumber, mono: true },
                  { label: 'Case title', value: record.title },
                  { label: 'Case status', value: CASE_STATUS_LABEL[record.status] },
                ]}
              />
            </ProfileCard>

            <ProfileCard
              id="case-profile-access-level"
              title="Access level"
              menuItems={[
                {
                  key: 'level',
                  label: 'Set access level',
                  permission: 'cases.set-access-level',
                  disabledReason: hasFlag(flags, FLAG.edit) ? null : 'You have not been given Edit on this case.',
                  onClick: () => setModal('level'),
                },
              ]}
            >
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Level', value: record.accessLevelName ?? 'Unclassified' },
                  {
                    label: 'Rank',
                    value: record.accessLevelRank != null ? <span className="font-mono tabular-nums">{record.accessLevelRank}</span> : null,
                  },
                  {
                    label: 'Read by',
                    value:
                      record.accessLevelId != null
                        ? `Members tagged ${record.accessLevelName} or stronger`
                        : 'Every member of the case',
                  },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="case-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: record.createdByName },
                  {
                    label: 'Created',
                    value: (
                      <time dateTime={toIso(record.createdAt)} className="font-mono">
                        {formatDateTime(record.createdAt)}
                      </time>
                    ),
                  },
                  { label: 'Updated by', value: record.updatedByName },
                  {
                    label: 'Updated',
                    value: record.updatedAt ? (
                      <time dateTime={toIso(record.updatedAt)} className="font-mono">
                        {formatDateTime(record.updatedAt)}
                      </time>
                    ) : null,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : null}

        {tab === 'attachments' ? (
          // The bleed panel's inset — the table fills it and scrolls its own rows.
          <div className="flex h-full min-h-0 min-w-0 flex-col p-4">
            <CaseFilesPanel
              id="case-files"
              files={files}
              maxBytes={CASE_FILE_MAX_BYTES}
              recordLabel={record.caseNumber}
              attachPermission="cases.attach"
              downloadPermission="cases.download"
              writeReason={
                !record.isActive
                  ? 'This case is deleted.'
                  : hasFlag(flags, FLAG.upload)
                    ? null
                    : 'You have not been given Upload on this case.'
              }
            />
          </div>
        ) : null}

        {tab === 'reports' ? (
          <div className="flex h-full min-h-0 min-w-0 flex-col p-4">
            <CaseReportsPanel caseId={record.id} caseIsActive={record.isActive} flags={flags} />
          </div>
        ) : null}

        {tab === 'team' ? (
          // The bleed panel's inset — the table fills it and scrolls its own rows.
          <div className="flex h-full min-h-0 min-w-0 flex-col p-4">
            <CaseTeam
              key={membersKey}
              caseId={record.id}
              shareReason={
                !record.isActive
                  ? 'This case is deleted.'
                  : hasFlag(flags, FLAG.share)
                    ? null
                    : 'You have not been given Share on this case.'
              }
              onShare={() => setModal('share')}
            />
          </div>
        ) : null}
      </ProfileHeaderShell>

      {modal === 'edit' ? (
        <CaseModal
          editing={record}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            toast.success(`${record.caseNumber} was saved.`);
            load();
          }}
        />
      ) : null}

      {modal === 'level' ? (
        <CaseAccessLevelModal
          record={record}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            toast.success('The access level was saved.');
            setRecord(null);
            load();
          }}
        />
      ) : null}

      {modal === 'share' ? (
        <CaseMembersModal
          caseId={record.id}
          caseNumber={record.caseNumber}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            toast.success(`Who ${record.caseNumber} is shared with was saved.`);
            setMembersKey((k) => k + 1);
          }}
        />
      ) : null}

      {modal === 'history' ? (
        <HistoryLogsModal
          open
          onClose={() => setModal(null)}
          module="cases"
          entityTypes={CASE_AUDIT_ENTITY_TYPES}
          entityId={record.id}
        />
      ) : null}

      {/* A banner action that WRITES confirms — no password: a restore is the undo. */}
      {modal === 'restore' ? (
        <ConfirmDialog
          open
          onClose={() => setModal(null)}
          onConfirm={() => {
            setModal(null);
            void restore();
          }}
          title="Restore case"
          requireConfirm
          loading={acting}
          id="case-profile-restore-dialog"
          body={
            <p>
              {record.caseNumber} appears in the case lists again for everyone it is shared with. A
              restore is refused when a live case has taken its number meanwhile.
            </p>
          }
        />
      ) : null}

      {modal === 'delete' ? (
        <ConfirmDialog
          open
          onClose={() => {
            setModal(null);
            setActionError(null);
          }}
          onConfirm={(reauthentication) => void deactivate(reauthentication)}
          title="Delete case"
          danger
          requireConfirm
          requireCredential
          loading={acting}
          id="case-profile-deactivate-dialog"
          body={
            <p>
              {record.caseNumber} stops appearing in the case lists for everyone it is shared with.
              Nothing is erased, and a deleted case can be restored.
            </p>
          }
        />
      ) : null}
    </div>
  );
}

/** Who the case is shared with. Read here; changed through the share dialog. */
function CaseTeam({
  caseId,
  shareReason,
  onShare,
}: {
  caseId: string;
  shareReason: string | null;
  onShare: () => void;
}) {
  const [summary, setSummary] = useState<CaseMemberSummary | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    casesApi
      .members(caseId, controller.signal)
      .then((next) => {
        if (!controller.signal.aborted) setSummary(next);
      })
      .catch((caught: unknown) => {
        if (!controller.signal.aborted) setError(caught instanceof ApiError ? caught : new ApiError('Could not load the team.', 0));
      });

    return () => controller.abort();
  }, [caseId]);

  const rows = useMemo(() => summary?.members ?? [], [summary]);

  // The section arrives WHOLE, so the table is handed it as one page.
  const fetchPage = useCallback(async () => ({ items: rows, total: rows.length }), [rows]);
  const state = useDataTable<CaseMemberRow>({ fetchPage, pageSize: 25, enabled: summary !== null });

  if (error) return <ErrorState message={error.message} correlationId={error.correlationId} />;

  return (
    <DataTablePanel
      id="case-team"
      fill
      state={state}
      columns={TEAM_COLUMNS}
      rowKey={(row) => row.userId}
      searchPlaceholder="Search the team"
      selectable={false}
      auditColumns={false}
      onCreate={onShare}
      createLabel="Share"
      createPermission="cases.share"
      createDisabledReason={shareReason}
      emptyMessage={
        summary?.creatorName
          ? `Only ${summary.creatorName}, who registered the case, can reach it. Share it to add employees.`
          : 'Nobody else has been given access to this case.'
      }
      emptySearchMessage="Nobody on the team matches that search."
    />
  );
}

const TEAM_COLUMNS: DataColumn<CaseMemberRow>[] = [
  { key: 'displayName', header: 'Employee', width: 240, render: (row) => <TextCell value={row.displayName} /> },
  { key: 'employeeNumber', header: 'Employee No.', width: 150, render: (row) => <TextCell value={row.employeeNumber} mono /> },
  {
    key: 'permissions',
    header: 'May',
    width: 340,
    summary: true,
    render: (row) => (
      <TextCell
        value={FLAG_NAMES.filter(([flag]) => hasFlag(row.permissions, flag))
          .map(([, name]) => name)
          .join(', ')}
      />
    ),
  },
];
